/**
 * Persistent banner when a foundation paste import is running or recently finished.
 */

import { FC } from 'react'
import { AlertTriangle, CheckCircle2, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useFoundationPasteImport } from '../context/FoundationPasteImportContext'

export const FoundationPasteImportBanner: FC = () => {
    const { showBanner, progress, openModal, dismissBanner, clearImportSession, recoverStuckImport } =
        useFoundationPasteImport()

    if (!showBanner || !progress) return null

    const isActive = progress.status === 'queued' || progress.status === 'running'
    const isComplete = progress.status === 'complete'
    const isFailed = progress.status === 'failed'

    const tone = isFailed
        ? 'border-red-200 bg-red-50 text-red-900'
        : isComplete
          ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
          : 'border-cz-purple-200 bg-cz-purple-50 text-cz-purple-900'

    return (
        <div className={`mx-4 sm:mx-6 mb-4 rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 ${tone}`}>
            <div className='flex items-start gap-3 flex-1 min-w-0'>
                {isActive ? (
                    <Loader2 className='w-5 h-5 shrink-0 animate-spin mt-0.5' />
                ) : isComplete ? (
                    <CheckCircle2 className='w-5 h-5 shrink-0 mt-0.5' />
                ) : (
                    <AlertTriangle className='w-5 h-5 shrink-0 mt-0.5' />
                )}
                <div className='min-w-0'>
                    <p className='text-sm font-semibold'>
                        {isActive
                            ? 'Building foundation from pasted text'
                            : isComplete
                              ? 'Paste import finished'
                              : 'Paste import failed'}
                    </p>
                    <p className='text-sm opacity-90 truncate'>
                        {isActive
                            ? `${progress.label || 'Working…'} — you can close the modal; import continues in the background.`
                            : isComplete
                              ? 'Review the summary and warnings, then refresh your profile sections.'
                              : progress.error || 'Open progress to try again or start a new import.'}
                    </p>
                </div>
            </div>
            <div className='flex items-center gap-2 shrink-0'>
                <Button type='button' size='sm' variant='primary' onClick={() => openModal('progress')}>
                    View progress
                </Button>
                {isActive && progress.step === 'briefs' && (
                    <Button
                        type='button'
                        size='sm'
                        variant='primary'
                        onClick={() => void recoverStuckImport()}
                    >
                        Finish import
                    </Button>
                )}
                {isActive && (
                    <Button
                        type='button'
                        size='sm'
                        variant='outline'
                        onClick={() => {
                            void clearImportSession().then(() => openModal('paste'))
                        }}
                    >
                        Clear stuck
                    </Button>
                )}
                {!isActive && (
                    <button
                        type='button'
                        onClick={dismissBanner}
                        className='p-1.5 rounded-lg hover:bg-black/5'
                        aria-label='Dismiss'
                    >
                        <X className='w-4 h-4' />
                    </button>
                )}
            </div>
        </div>
    )
}
